import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { formatLocationType } from "@/lib/format";
import { cn } from "@/lib/utils";
import { getLocations, getSuggestedPlays } from "@/queries/playlists";
import { getTeams } from "@/queries/team";
import { useSessionData } from "@/stores/SessionDataContext";
import { format } from "date-fns";
import { CalendarIcon, GripVertical, Plus, Trash } from "lucide-react";
import React, { useState } from "react";
import { Controller, useFieldArray, UseFormReturn } from "react-hook-form";
import * as z from "zod";

// @dnd-kit imports
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const actionSchema = z.object({
  title: z.string().min(1, "Action title is required"),
  team_id: z.number().nullable(),
  order: z.number().default(0),
});

export const adHocPlaySchema = z
  .object({
    title: z.string().min(1, "Title is required"),
    category: z.string().min(1, "Category is required"),
    location_type: z.enum(
      [
        "terminal",
        "gate",
        "ticketing",
        "checkpoint",
        "security_lane",
        "restroom",
        "customs",
        "intl_baggage",
        "baggage_carousel",
        "door",
        "other",
      ],
      {
        required_error: "Please select a location type",
      }
    ),
    location_details: z.string().nullable(),
    terminal: z.string().nullable(),
    concourse: z.string().nullable(),
    start_interval: z.number().min(0).max(144, "Invalid time interval"),
    end_interval: z.number().min(0).max(144, "Invalid time interval"),
    target_date: z.date().optional(),
    actions: z.array(actionSchema).min(1, "At least one action is required"),
  })
  .refine((data) => data.end_interval >= data.start_interval, {
    message: "End time must be after start time",
    path: ["end_interval"],
  });

export type AdHocPlayFormValues = z.infer<typeof adHocPlaySchema>;

// Separate type for API submission with date as string
export type AdHocPlayFormSubmission = Omit<
  AdHocPlayFormValues,
  "target_date"
> & {
  target_date: string;
};

interface AdHocPlayFormProps {
  form: UseFormReturn<AdHocPlayFormValues>;
  onSubmit: (values: AdHocPlayFormSubmission) => void;
  isLoading: boolean;
  submitButtonText: string;
  onClose?: () => void;
}

// Separate ActionItem component for drag and drop
function ActionItem({
  id,
  index,
  onRemove,
  control,
  teamsList,
  errors,
}: {
  id: string;
  index: number;
  onRemove: () => void;
  control: any;
  teamsList: any[];
  errors: any;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`space-y-2 p-4 border border-slate-600 rounded-lg transition-all duration-200 ${
        isDragging ? "opacity-50 scale-95 z-50" : "opacity-100 scale-100"
      }`}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <div
            className="cursor-grab active:cursor-grabbing touch-none"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4 text-gray-400" />
          </div>
          <h4 className="text-sm font-medium text-slate-200">
            Action {index + 1}
          </h4>
        </div>
        <Button type="button" variant="ghost" size="icon" onClick={onRemove}>
          <Trash className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Action Description</Label>
        <Controller
          name={`actions.${index}.title`}
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              placeholder={`Describe what should be done for action ${index + 1}`}
              className="bg-slate-800 text-slate-100 border-slate-700"
            />
          )}
        />
        {errors.actions?.[index]?.title && (
          <p className="text-red-400 text-sm">
            {errors.actions[index]?.title?.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Assign to Team (Optional)</Label>
        <Controller
          name={`actions.${index}.team_id`}
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={(value) =>
                field.onChange(value ? parseInt(value) : null)
              }
              value={field.value?.toString() || ""}
            >
              <SelectTrigger className="bg-slate-800 text-slate-100 border-slate-700">
                <SelectValue placeholder="Select team (optional)" />
              </SelectTrigger>
              <SelectContent>
                {teamsList.map((team) => (
                  <SelectItem key={team.id} value={team.id.toString()}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {errors.actions?.[index]?.team_id && (
          <p className="text-red-400 text-sm">
            {errors.actions[index]?.team_id?.message}
          </p>
        )}
      </div>
    </div>
  );
}

export function AdHocPlayForm({
  form,
  onSubmit,
  isLoading,
  submitButtonText,
  onClose,
}: AdHocPlayFormProps) {
  const [showTerminal, setShowTerminal] = useState(false);
  const [showConcourse, setShowConcourse] = useState(false);
  const { airport_config, terminals, concourses } = useSessionData();

  // Fetch teams for dropdown
  const { data: teams } = getTeams();
  const teamsList = teams || [];

  // Transform terminals and concourses into dropdown options
  const terminalOptions = terminals.map((t) => ({
    display: t.terminal_name,
    value: t.terminal_name,
  }));

  const concourseOptions = concourses.map((c) => ({
    display: c.name,
    value: c.name,
  }));

  // Fetch suggested plays
  const { data: suggestedPlaysData } = getSuggestedPlays();
  const suggestedPlays = suggestedPlaysData?.suggested_plays || [];

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = form;

  // Set default target_date to today if not already set
  React.useEffect(() => {
    const currentTargetDate = getValues("target_date");
    if (!currentTargetDate) {
      setValue("target_date", new Date());
    }
  }, [setValue, getValues]);

  // Watch location_type to fetch available locations
  const selectedLocationType = watch("location_type");
  const { data: locationsData } = getLocations(
    selectedLocationType ? [selectedLocationType] : []
  );
  const availableLocations =
    locationsData?.locations?.[selectedLocationType] || [];

  // Handle suggested play selection
  const handleSuggestedPlaySelect = (playId: string) => {
    if (!playId) return;

    const selectedPlay = suggestedPlays.find(
      (play: any) => play.id.toString() === playId
    );
    if (!selectedPlay) return;

    // Fill form with suggested play data
    setValue("title", selectedPlay.title);
    setValue("category", selectedPlay.category);
    setValue("location_type", selectedPlay.location_type);
    setValue("location_details", selectedPlay.location_details || "");
    setValue("terminal", selectedPlay.terminal || "");
    setValue("concourse", selectedPlay.concourse || "");
    setValue("start_interval", selectedPlay.start_interval);
    setValue("end_interval", selectedPlay.end_interval);
    // Keep the current target_date instead of using suggested play's date
  };

  // @dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "actions",
  });

  // Handle drag end event
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = fields.findIndex((field) => field.id === active.id);
      const newIndex = fields.findIndex((field) => field.id === over?.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        // Update order values after reordering
        const currentActions = getValues("actions");
        const reorderedActions = arrayMove(currentActions, oldIndex, newIndex);
        const actionsWithUpdatedOrder = reorderedActions.map(
          (action, index) => ({
            ...action,
            order: index,
          })
        );
        setValue("actions", actionsWithUpdatedOrder);
      }
    }
  };

  const generateTimeOptions = () => {
    const options = [];
    for (let i = 0; i <= 144; i++) {
      const hours = Math.floor((i * 10) / 60);
      const minutes = (i * 10) % 60;
      const time = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
      options.push({ value: i, label: time });
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  const handleFormSubmit = (values: AdHocPlayFormValues) => {
    // Ensure actions have sequential order values
    const actionsWithOrder = values.actions
      .filter((action) => action.title.trim() !== "")
      .map((action, index) => ({
        title: action.title,
        team_id: action.team_id,
        order: index,
      }));

    // Format target_date as ISO string for API
    const formattedDate = values.target_date
      ? format(values.target_date, "yyyy-MM-dd")
      : format(new Date(), "yyyy-MM-dd");

    onSubmit({
      ...values,
      target_date: formattedDate,
      actions: actionsWithOrder,
    });
  };

  return (
    <div className="w-full mx-auto">
      <form onSubmit={handleSubmit(handleFormSubmit)} className="h-full">
        <div className="grid grid-cols-5 gap-8 h-full">
          {/* First Column - Form Fields (3/5 width) */}
          <div className="col-span-3 space-y-4">
            {/* Suggested Plays Dropdown */}
            {suggestedPlays.length > 0 && (
              <div className="space-y-2">
                <Label className="text-slate-200">
                  Quick Start - Use Suggested Play
                </Label>
                <Select onValueChange={handleSuggestedPlaySelect}>
                  <SelectTrigger className="bg-slate-800 text-slate-100 border-slate-700 h-12">
                    <SelectValue placeholder="Select a suggested play to auto-fill form..." />
                  </SelectTrigger>
                  <SelectContent>
                    {suggestedPlays.map((play: any) => (
                      <SelectItem key={play.id} value={play.id.toString()}>
                        <div className="flex flex-col items-start p-1">
                          <span className="font-medium">{play.title}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatLocationType(play.location_type)} •{" "}
                            {play.location_details}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="title" className="text-slate-200">
                Title
              </Label>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    className="bg-slate-800 text-slate-100 border-slate-700"
                  />
                )}
              />
              {errors.title && (
                <p className="text-red-400 text-sm">{errors.title.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="category" className="text-slate-200">
                Category
              </Label>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    className="w-full rounded-md bg-slate-800 text-slate-100 border-slate-700 p-2"
                  >
                    <option value="General">General</option>
                  </select>
                )}
              />
              {errors.category && (
                <p className="text-red-400 text-sm">
                  {errors.category.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="location_type" className="text-slate-200">
                Location Type
              </Label>
              <Controller
                name="location_type"
                control={control}
                render={({ field }) => (
                  <Select
                    onValueChange={(value) => field.onChange(value)}
                    value={field.value}
                  >
                    <SelectTrigger className="bg-slate-800 text-slate-100 border-slate-700">
                      <SelectValue placeholder="Select location type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="terminal">Terminal</SelectItem>
                      <SelectItem value="gate">Gate Lounge</SelectItem>
                      <SelectItem value="ticketing">Ticketing</SelectItem>
                      <SelectItem value="checkpoint">Checkpoint</SelectItem>
                      <SelectItem value="security_lane">
                        Security Lane
                      </SelectItem>
                      <SelectItem value="restroom">Restroom</SelectItem>
                      <SelectItem value="customs">Customs</SelectItem>
                      <SelectItem value="intl_baggage">
                        International Baggage
                      </SelectItem>
                      <SelectItem value="baggage_carousel">
                        Baggage Carousel
                      </SelectItem>
                      <SelectItem value="door">Door</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.location_type && (
                <p className="text-red-400 text-sm">
                  {errors.location_type.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="location_details" className="text-slate-200">
                Location Name
              </Label>
              <Controller
                name="location_details"
                control={control}
                render={({ field }) => (
                  <Select
                    onValueChange={(value) => field.onChange(value)}
                    value={field.value || ""}
                  >
                    <SelectTrigger className="bg-slate-800 text-slate-100 border-slate-700">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.isArray(availableLocations)
                        ? availableLocations.map((location: any) => (
                            <SelectItem key={location.id} value={location.name}>
                              {location.name}
                            </SelectItem>
                          ))
                        : null}
                    </SelectContent>
                  </Select>
                )}
              />
              <p className="text-sm text-slate-600">
                Hint: Select a specific location from the dropdown. Available
                locations are based on your selected location type.
              </p>
            </div>

            {!showTerminal && (
              <Button
                type="button"
                variant="link"
                className="p-0 text-slate-300 hover:text-slate-100"
                onClick={() => setShowTerminal(true)}
              >
                [+] Associate with Terminal
              </Button>
            )}
            {showTerminal && (
              <div className="space-y-2">
                <Label htmlFor="terminal" className="text-slate-200">
                  Terminal
                </Label>
                <Controller
                  name="terminal"
                  control={control}
                  render={({ field }) => (
                    <Select
                      onValueChange={(value) => field.onChange(value)}
                      value={field.value || ""}
                    >
                      <SelectTrigger className="bg-slate-800 text-slate-100 border-slate-700">
                        <SelectValue placeholder="Select terminal" />
                      </SelectTrigger>
                      <SelectContent>
                        {terminalOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.display}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            )}
            {airport_config.has_concourse && (
              <div>
                {!showConcourse && (
                  <Button
                    type="button"
                    variant="link"
                    className="p-0 text-slate-300 hover:text-slate-100"
                    onClick={() => setShowConcourse(true)}
                  >
                    [+] Associate with Concourse
                  </Button>
                )}
                {showConcourse && (
                  <div className="space-y-2">
                    <Label htmlFor="concourse" className="text-slate-200">
                      Concourse
                    </Label>
                    <Controller
                      name="concourse"
                      control={control}
                      render={({ field }) => (
                        <Select
                          onValueChange={(value) => field.onChange(value)}
                          value={field.value || ""}
                        >
                          <SelectTrigger className="bg-slate-800 text-slate-100 border-slate-700">
                            <SelectValue placeholder="Select concourse" />
                          </SelectTrigger>
                          <SelectContent>
                            {concourseOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.display}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="start_interval" className="text-slate-200">
                Start Time
              </Label>
              <Controller
                name="start_interval"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    className="w-full rounded-md bg-slate-800 text-slate-100 border-slate-700 p-2"
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  >
                    {timeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.start_interval && (
                <p className="text-red-400 text-sm">
                  {errors.start_interval.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="end_interval" className="text-slate-200">
                End Time
              </Label>
              <Controller
                name="end_interval"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    className="w-full rounded-md bg-slate-800 text-slate-100 border-slate-700 p-2"
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  >
                    {timeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.end_interval && (
                <p className="text-red-400 text-sm">
                  {errors.end_interval.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="target_date" className="text-slate-200">
                Target Date
              </Label>
              <Controller
                name="target_date"
                control={control}
                render={({ field }) => (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal bg-slate-800 text-slate-100 border-slate-700 hover:bg-slate-700",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {field.value ? (
                          format(new Date(field.value), "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={
                          field.value ? new Date(field.value) : undefined
                        }
                        onSelect={(date) => field.onChange(date)}
                        disabled={(date) =>
                          date < new Date(new Date().setHours(0, 0, 0, 0))
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                )}
              />
              {errors.target_date && (
                <p className="text-red-400 text-sm">
                  {errors.target_date.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-green-600 hover:bg-green-500 text-white"
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : submitButtonText}
            </Button>
          </div>

          {/* Second Column - Actions (2/5 width) */}
          <div className="col-span-2 space-y-4">
            <div>
              <Label className="text-slate-200">Execute Actions</Label>
            </div>

            <div className="max-h-[632px] overflow-y-auto pr-2 space-y-4">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={fields.map((field) => field.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {fields.map((field, index) => (
                    <ActionItem
                      key={field.id}
                      id={field.id}
                      index={index}
                      onRemove={() => remove(index)}
                      control={control}
                      teamsList={teamsList}
                      errors={errors}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={() =>
                append({ title: "", team_id: null, order: fields.length })
              }
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Action
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
